/**
 * Locale store — single source of truth for the user's UI language.
 *
 * Detection chain (highest priority first):
 *   1. authenticated `user.locale` (from /auth/me) — server-stored preference
 *   2. localStorage `vistochat:locale` — per-device preference for guests
 *   3. navigator.language → 'el' if starts with 'el', else 'en'
 *   4. fallback 'en'
 *
 * When the user toggles language via the AppShell switcher:
 *   - the i18n instance flips immediately
 *   - <html lang> is updated
 *   - localStorage is written
 *   - PATCH /auth/locale persists to backend if authenticated
 */
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { i18n, LOCALE_STORAGE_KEY, type AppLocale } from '@/locales';
import { authApi } from '@/services/api';
import { useAuthStore } from './auth';

export const useLocaleStore = defineStore('locale', () => {
  const authStore = useAuthStore();
  const current = ref<AppLocale>((i18n.global.locale.value as AppLocale) || 'en');

  function applyLocaleClientOnly(locale: AppLocale) {
    current.value = locale;
    i18n.global.locale.value = locale;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      /* ignore storage errors */
    }
  }

  /**
   * Public switcher. Updates UI immediately, persists to backend if logged in.
   * Best-effort: a PATCH failure leaves localStorage updated so the choice
   * still applies on this device.
   */
  async function setLocale(locale: AppLocale) {
    if (locale === current.value) return;
    applyLocaleClientOnly(locale);
    if (authStore.isAuthenticated) {
      try {
        await authApi.setLocale(locale);
        // Mirror onto in-memory user object so a subsequent `fetchMe` doesn't
        // flip the UI back to the previous server value during the sync window.
        if (authStore.user) authStore.user.locale = locale;
      } catch {
        /* swallow — localStorage already updated, will retry next change */
      }
    }
  }

  /**
   * Bootstrap: apply the initial locale picked by `detectInitialLocale()` to
   * the DOM (<html lang>). The i18n instance was already initialized with
   * this locale, so no UI flicker.
   */
  applyLocaleClientOnly(current.value);

  /**
   * Sync from authenticated user. When `/auth/me` resolves and the user has a
   * locale that differs from the current one, adopt the server value — but
   * ONLY on initial login (not when the user manually switched and we already
   * pushed it to the server). To distinguish: we always immediately mirror
   * onto `authStore.user.locale` on switch, so this watcher fires only when
   * the SERVER returns a different value than what we have locally.
   */
  watch(
    () => authStore.user?.locale as AppLocale | undefined,
    (userLocale) => {
      if (!userLocale) return;
      if (userLocale !== 'en' && userLocale !== 'el') return;
      if (userLocale === current.value) return;
      applyLocaleClientOnly(userLocale);
    },
  );

  return { current, setLocale };
});
